import { Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { AngularFireStorage } from "@angular/fire/compat/storage";
import * as moment from "moment";
import { BehaviorSubject, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { RelevantLabDocument } from "../models/relevant-lab-document.model";
import { RelevantLab } from "../models/relevant-lab.model";
import * as utils from '../utils/utility-functions';


@Injectable({
    providedIn: 'root',
})

export class RelevantLabService {
    private relevantLabView = new BehaviorSubject(new RelevantLab);
    currentRelevantLabView = this.relevantLabView.asObservable();

    private relevantLabList = new BehaviorSubject([]);
    currentRelevantLabList = this.relevantLabList.asObservable();

    private bookingRelevantLabListTemp = new BehaviorSubject([]);
    currentBookingRelevantLabListTemp = this.bookingRelevantLabListTemp.asObservable();

    constructor(
        private angularFirestore: AngularFirestore,
        private storage: AngularFireStorage,
        private afAuth: AngularFireAuth
    ) { }

    getRelevantLabByPatientIDAppointmentIDAsync(patientID: string, appointmentID) {
        return this.afAuth.authState.pipe(
            switchMap(user => {
                if (user) {
                    return this.angularFirestore.collection<any>('Labs', ref => ref.where('patient_id', '==', patientID).where('appointment_id', '==', appointmentID)).valueChanges({ idField: 'id' });
                } else {
                    return of(null);
                }
            })
        );
    }

    async getRelevantLabByID(relevantLabID: string) {
        try {
            let relevantLab = null;

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Labs');

            await collection.ref
                .doc(relevantLabID)
                .get()
                .then(querySnapshot => {
                    const docData = {
                        id: querySnapshot.id,
                        ...querySnapshot.data()
                    }
                    relevantLab = docData;
                }).catch(err => console.log("getRelevantLabByID query 1 error: " + err));

            this.updateRelevantLabView(relevantLab);
            return relevantLab;
        } catch (error) {
            console.log('getRelevantLabByID(): ' + JSON.stringify(error));
        }
    }

    private async getRelevantLabByPatientIDAppointmentID(patientID: string, appointmentID) {
        try {
            let relevantLabList = [];

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Labs');

            await collection.ref
                .where('patient_id', '==', patientID)
                .where('appointment_id', '==', appointmentID)
                .get()
                .then(querySnapshot => {
                    querySnapshot.forEach(doc => {
                        const docData = {
                            id: doc.id,
                            ...doc.data()
                        }
                        relevantLabList.push(docData)
                    });
                }).catch(err => console.log("getRelevantLabByPatientIDAppointmentID query 1 error: " + err));

            this.updateRelevantLabList(relevantLabList);
            return relevantLabList;
        } catch (error) {
            console.log('getRelevantLabByPatientIDAppointmentID(): ' + JSON.stringify(error));
        }
    }

    async saveBookingRelevantLabWithoutFile(relevantLab) {
        try {
            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Labs');

            let relevantLabUpdated = relevantLab;

            await collection.ref
                .add(JSON.parse(JSON.stringify(relevantLabUpdated)))
                .then(function (docRef) {
                    relevantLabUpdated.id = docRef.id
                })
                .catch(err => console.log("saveBookingRelevantLabWithoutFile query 1 error: " + err));

            const tempBookingLabs = this.bookingRelevantLabListTemp.getValue();
            tempBookingLabs.push(relevantLabUpdated);
            this.bookingRelevantLabListTemp.next(tempBookingLabs);

            return true;
        } catch (error) {
            console.log('saveBookingRelevantLabWithoutFile(): ' + JSON.stringify(error));
        }
    }

    async saveBookingRelevantLabWeb(file: any, relevantLab) {
        try {
            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Labs');

            let downloadURL;
            let relevantLabUpdated = relevantLab;

            const fileName = moment().format('x').toString() + '_' + 'lab';
            const remotePath = 'uploads/images/' + fileName;
            const fileRef = this.storage.ref(remotePath);

            await this.storage.upload(remotePath, file)
                .catch((error) => {
                    console.log('saveImage upload error', error)
                });

            await fileRef.getDownloadURL().toPromise()
                .then((result) => {
                    downloadURL = result;
                })
                .catch((error) => {
                    console.log('saveImage getDownloadURL error', error)
                });

            let relevantLabDocument = new RelevantLabDocument();
            relevantLabDocument.document_attached = downloadURL;
            relevantLabDocument.document_name = fileName;
            relevantLabDocument.document_type = "image";
            relevantLabUpdated.documents.push(relevantLabDocument);


            await collection.ref
                .add(JSON.parse(JSON.stringify(relevantLabUpdated)))
                .then(function (docRef) {
                    relevantLabUpdated.id = docRef.id
                })
                .catch(err => console.log("saveBookingRelevantLabWeb query 1 error: " + err));

            const tempBookingLabs = this.bookingRelevantLabListTemp.getValue();
            tempBookingLabs.push(relevantLabUpdated);
            this.bookingRelevantLabListTemp.next(tempBookingLabs);

            const bookingLabs = this.relevantLabList.getValue();
            bookingLabs.push(relevantLabUpdated);

            this.updateRelevantLabList(bookingLabs);
            return true;
        } catch (error) {
            console.log('saveBookingRelevantLabWeb(): ' + JSON.stringify(error));
        }
    }

    async saveRelevantLabWithoutFile(relevantLab) {
        try {
            let relevantLabUpdated = relevantLab;

            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Labs');

            await collection.ref
                .add(JSON.parse(JSON.stringify(relevantLabUpdated)))
                .then(function (docRef) {
                    relevantLabUpdated.id = docRef.id;
                })
                .catch(err => console.log("saveRelevantLabWithoutFile save 1 error: " + err));

            const tempBookingLabs = this.relevantLabList.getValue();
            tempBookingLabs.push(relevantLab);
            this.relevantLabList.next(tempBookingLabs);

            return true;
        } catch (error) {
            console.log('saveRelevantLabWithoutFile(): ' + JSON.stringify(error));
        }
    }

    private async saveBookingLabsAsync(tempLabs) {
        try {
            const collection: AngularFirestoreCollection = this.angularFirestore.collection('Labs');

            await collection.ref
                .add(JSON.parse(JSON.stringify(tempLabs)))
                .catch(err => console.log("saveBookingLabsAsync query 1 error: " + err));

        } catch (error) {
            console.log('saveBookingLabsAsync(): ' + JSON.stringify(error));
        }
    }

    saveBookingLabs(appointmentID: string) {
        let tempBookingLabs = this.bookingRelevantLabListTemp.getValue();
        this.bookingRelevantLabListTemp.next([]);

        tempBookingLabs.forEach(lab => {
            lab.appointment_id = appointmentID
            this.saveBookingLabsAsync(lab);
        });
    }

    private updateRelevantLabList(relevantLabs: any) {
        let relevantLabListTemp: RelevantLab[] = [];
        relevantLabs.forEach(relevantLab => {
            let newRevelantLab: RelevantLab = utils.createRelevantLab(relevantLab);
            relevantLabListTemp.push(newRevelantLab);
        });
        this.relevantLabList.next(relevantLabListTemp);
    }

    private updateRelevantLabView(relevantLab: any) {
        let newRevelantLab: RelevantLab = utils.createRelevantLab(relevantLab);
        this.relevantLabView.next(newRevelantLab);
    }

    setPatientIdAppointmentId(patientID: string, appointmentID: string) {
        this.getRelevantLabByPatientIDAppointmentID(patientID, appointmentID)
    }
}